import {
  ContextualValue,
  IContextualRequestBag
} from 'common/schema/contextual-value/ContextualValue';
import { isContextualValueEmpty } from 'common/schema/contextual-value/isContextualValueEmpty';
import { MetadataConditionalLogic } from 'common/schema/metadata/conditional/MetadataConditionalLogic';
import { IMetadata } from 'common/schema/metadata/Metadata';
import { IWidgetInput } from 'common/schema/widget/input/WidgetInput';
import moment from 'moment';
import * as yup from 'yup';

declare module 'yup' {
  interface StringSchema {
    /**
     * Permette a una stringa di essere presa di default dai metadati
     * contestuali
     * Gestisce se il campo è richiesto in caso di metadato condizionale
     */
    requiredMetadata(metadata: IMetadata, input: IWidgetInput): StringSchema;
  }

  interface NumberSchema {
    /**
     * Permette a un numero di essere preso di default dai metadati
     * contestuali
     * Gestisce se il campo è richiesto in caso di metadato condizionale
     */
    requiredMetadata(metadata: IMetadata, input: IWidgetInput): NumberSchema;
  }

  interface DateSchema {
    /**
     * Permette a una data di essere presa di default dai metadati
     * contestuali
     * Gestisce se il campo è richiesto in caso di metadato condizionale
     */
    requiredMetadata(metadata: IMetadata, input: IWidgetInput): DateSchema;
  }

  interface BooleanSchema {
    /**
     * Permette a una data di essere presa di default dai metadati
     * contestuali
     * Gestisce se il campo è richiesto in caso di metadato condizionale
     */
    requiredMetadata(metadata: IMetadata, input: IWidgetInput): BooleanSchema;
  }

  interface NotRequiredArraySchema<T> {
    /**
     * Permette a una data di essere presa di default dai metadati
     * contestuali
     */
    requiredMetadata(
      metadata: IMetadata,
      input: IWidgetInput
    ): NotRequiredArraySchema<T>;
  }
}

/**
 * Crea la validazione `required` in base al tipo di schema
 */
function makeRequired(schema: yup.MixedSchema<any>) {
  // Per i booleani
  if (schema.type === 'boolean')
    return schema.required().oneOf([true], 'Il campo ${path} è obbligatorio');

  // Tutti gli altri
  return schema.required();
}

yup.addMethod(yup.mixed, 'requiredMetadata', function (
  this: yup.MixedSchema<any>,
  metadata: IMetadata,
  input: IWidgetInput
) {
  const type = metadata.contextual;

  // In caso non siano specificati
  // ne un valore contestuale ne un valore condizionale (per mostrare il campo)
  // impostiamo il campo a richiesto. [spid: null, cond: null]
  if (!type && !input.conditional.metadataId) return makeRequired(this);

  // Altrimenti impostiamo la condizione con Yup
  return this.when(
    ['$request', input.conditional.metadataId ?? 'this-is-not-an-id'],
    (
      request: IContextualRequestBag,
      conditionValue: string,
      schema: yup.MixedSchema<any>
    ) => {
      // se è specificato il dato contestuale
      if (type) {
        const context = ContextualValue.find(type);
        const contextualValue = context.fromRequest(request);

        // se abbiamo già il valore non è necessario impostare il campo a richieso
        // [spid: valida, cond: null | esiste | valida]
        if (!isContextualValueEmpty(contextualValue)) {
          return (
            schema
              .notRequired()
              // Impostiamo il valore di default del campo con il valore che abbiamo
              // ricevuto da SPID (valore contestuale). Questo ci permette di
              // applicare le validazioni sul valore di default, senza dover
              // modificare gli initialValues del form.
              // Riferimento: validazione data di nascita successiva al.
              // TODO Supportare anche se non è richiesto
              .default(ContextualValue.convertForYup(type, contextualValue))
          );
        }

        // altrimenti se il campo è visibile lo impostiamo a richiesto ? [spid: null, cond: valida] : [spid: null, cond: null | esiste]
      }
      // altrimenti se NON è specificato il dato contestuale
      // verifichiamo se il campo è visibile e lo impostiamo a richiesto ? [spid: null, cond: valida] : [spid: null, cond: null | esiste]
      return isFieldVisible(input, conditionValue)
        ? makeRequired(schema)
        : schema.notRequired();
    }
  );
});

function isFieldVisible(input: IWidgetInput, conditionValue: string): boolean {
  return input.conditional.metadataId
    ? MetadataConditionalLogic.isVerified(input.conditional, conditionValue)
      ? true
      : false
    : true;
}
